import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ChevronRight, ChevronDown, ArrowUp, ArrowDown, X, Table2,
  ChevronsDownUp, ChevronsUpDown, GripVertical, ChevronLeft,
} from 'lucide-react';
import { flattenTree, formatValue, formatAggValue, displayLabel } from '../../analytics-shared/engine/reportEngine';

const AGG_LABEL = { SUM: 'Σ', AVG: 'avg', MIN: 'min', MAX: 'max', COUNT: '#' };
const PREVIEW_LIMIT = 25;
const PAGE_SIZE = 50;

/**
 * Dynamic report table with grouping, subtotals and totals.
 * - builder mode: 25-row preview, editable headers (sort/remove/resize/reorder)
 * - view mode (readOnly + paged): full data with pagination, no structural edits
 */
const ReportTable = ({
  config, result, fieldMap, onSort,
  onRemoveColumn = () => {}, onReorderColumns = () => {},
  readOnly = false, paged = false,
}) => {
  // Defensive defaults — the engine returns a RunResult dict on success
  // but every consumer page renders ReportTable BEFORE the first run
  // resolves (result is null). Previously the very first access of
  // `safeResult.grouped` threw "Cannot read properties of null", which
  // bricked the builder until a hard reload. Falling through to a
  // synthetic empty result keeps the component pure and lets the
  // shared "no data yet" state below take care of presentation.
  const safeResult = result || {
    grouped: false,
    rows: [],
    records: [],
    recordCount: 0,
    grandTotals: {},
    tree: [],
  };
  const summaryKeys = Object.keys(config?.summaries || {});
  const displayColumns = useMemo(() => {
    const cols = [...((config && config.columns) || [])];
    summaryKeys.forEach((k) => { if (!cols.includes(k)) cols.push(k); });
    return cols;
  }, [config?.columns, summaryKeys]);

  const [expanded, setExpanded] = useState(() => new Set());
  const [widths, setWidths] = useState({});
  const [tablePage, setTablePage] = useState(1);
  const dragCol = useRef(null);
  const resizing = useRef(null);

  const treeKey = safeResult.grouped ? (safeResult.tree || []).map((n) => n.path).join('|') : '';
  useEffect(() => {
    if (safeResult.grouped) setExpanded(new Set((safeResult.tree || []).map((n) => n.path)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeKey]);
  useEffect(() => { setTablePage(1); }, [safeResult.recordCount, safeResult.grouped]);

  const toggleNode = (path) => setExpanded((s) => {
    const n = new Set(s);
    n.has(path) ? n.delete(path) : n.add(path);
    return n;
  });

  const allPaths = () => {
    const paths = [];
    const walk = (nodes) => nodes.forEach((n) => {
      paths.push(n.path);
      if (n.children) walk(n.children);
    });
    walk(safeResult.tree || []);
    return paths;
  };
  const expandAll = () => setExpanded(new Set(allPaths()));
  const collapseAll = () => setExpanded(new Set());

  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: widths[key] || 160 };
    const move = (ev) => {
      if (!resizing.current) return;
      const delta = ev.clientX - resizing.current.startX;
      setWidths((w) => ({ ...w, [resizing.current.key]: Math.max(80, resizing.current.startW + delta) }));
    };
    const up = () => {
      resizing.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (displayColumns.length === 0 && !safeResult.grouped) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Table2 className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-base font-medium text-slate-700">
          {readOnly ? 'No columns configured' : 'Build your report'}
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">
          {readOnly
            ? 'This report has no columns or grouping. Click Edit to configure it.'
            : 'Drag fields from the left into Columns, or drop a field into Group By to start summarizing.'}
        </p>
      </div>
    );
  }

  const groupRows = safeResult.grouped ? flattenTree(safeResult.tree, expanded, true) : [];
  let visibleGroupRows = groupRows;
  let visibleFlatRows = safeResult.grouped ? [] : safeResult.rows;
  let previewTruncated = false;
  let totalPages = 1;
  let safePage = 1;

  if (paged) {
    if (!safeResult.grouped) {
      totalPages = Math.max(1, Math.ceil(safeResult.rows.length / PAGE_SIZE));
      safePage = Math.min(tablePage, totalPages);
      visibleFlatRows = safeResult.rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    }
  } else {
    visibleGroupRows = groupRows.slice(0, PREVIEW_LIMIT);
    visibleFlatRows = safeResult.grouped ? [] : safeResult.rows.slice(0, PREVIEW_LIMIT);
    previewTruncated = safeResult.grouped
      ? groupRows.length > PREVIEW_LIMIT
      : safeResult.rows.length > PREVIEW_LIMIT;
  }

  const SummaryChips = ({ summaries, count }) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
        {count} records
      </span>
      {summaryKeys.map((k) => (
        <span key={k} className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
          {AGG_LABEL[config.summaries[k]]} {displayLabel(fieldMap[k])}: {formatAggValue(summaries[k], k, config.summaries[k], fieldMap)}
        </span>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 bg-white">
        <span className="text-xs text-slate-500">
          {previewTruncated
            ? `Preview — first ${PREVIEW_LIMIT} of ${safeResult.recordCount.toLocaleString()} rows`
            : `${safeResult.recordCount.toLocaleString()} rows`}
          {safeResult.grouped && ` • ${safeResult.tree.length} groups`}
        </span>
        {safeResult.grouped && (
          <div className="flex items-center gap-1">
            <button onClick={expandAll} className="flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 px-2 py-1 rounded hover:bg-slate-50">
              <ChevronsUpDown className="h-3.5 w-3.5" /> Expand all
            </button>
            <button onClick={collapseAll} className="flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 px-2 py-1 rounded hover:bg-slate-50">
              <ChevronsDownUp className="h-3.5 w-3.5" /> Collapse all
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              {displayColumns.map((key, idx) => {
                const field = fieldMap[key] || { label: key };
                const isSorted = config.sort && config.sort.field === key;
                const isSummary = summaryKeys.includes(key);
                return (
                  <th
                    key={key}
                    draggable={!readOnly}
                    onDragStart={readOnly ? undefined : () => { dragCol.current = idx; }}
                    onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
                    onDrop={readOnly ? undefined : () => {
                      if (dragCol.current != null && dragCol.current !== idx) {
                        onReorderColumns(dragCol.current, idx);
                      }
                      dragCol.current = null;
                    }}
                    className="relative text-left px-3 py-2 font-medium text-slate-600 whitespace-nowrap group select-none"
                    style={{ width: widths[key], minWidth: widths[key] || 120 }}
                  >
                    <div className="flex items-center gap-1.5">
                      {!readOnly && <GripVertical className="h-3 w-3 text-slate-300 cursor-grab" />}
                      <button onClick={() => onSort(key)} className="flex items-center gap-1 hover:text-slate-900">
                        {displayLabel(field) || field.label}
                        {isSummary && <span className="text-[10px] text-indigo-500">({AGG_LABEL[config.summaries[key]]})</span>}
                        {isSorted && (config.sort.dir === 'asc'
                          ? <ArrowUp className="h-3 w-3 text-indigo-600" />
                          : <ArrowDown className="h-3 w-3 text-indigo-600" />)}
                      </button>
                      {!readOnly && (
                        <button
                          onClick={() => onRemoveColumn(key)}
                          className="ml-auto opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"
                          title="Remove column"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {!readOnly && (
                      <div
                        onMouseDown={(e) => startResize(e, key)}
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-indigo-300"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {safeResult.grouped && visibleGroupRows.map((row, i) => {
              if (row.kind === 'group') {
                const { node } = row;
                const isOpen = expanded.has(node.path);
                const field = fieldMap[node.field] || { label: node.field };
                return (
                  <tr key={`g${i}`} className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/60 to-transparent hover:from-indigo-50">
                    <td colSpan={displayColumns.length || 1} className="px-2 py-2">
                      <div className="flex items-center gap-2" style={{ paddingLeft: node.level * 20 }}>
                        <button onClick={() => toggleNode(node.path)}
                          className="p-0.5 rounded hover:bg-indigo-100 text-slate-500">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{displayLabel(field) || field.label}</span>
                        <span className="text-sm font-semibold text-slate-900">{node.value}</span>
                        <div className="ml-2">
                          <SummaryChips summaries={node.summaries} count={node.count} />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              }
              const rec = row.record;
              return (
                <tr key={`r${i}`} className="border-b border-slate-50 hover:bg-slate-50">
                  {displayColumns.map((key, ci) => (
                    <td key={key} className="px-3 py-1.5 text-slate-700 whitespace-nowrap"
                      style={ci === 0 ? { paddingLeft: row.level * 20 + 12 } : undefined}>
                      {formatValue(rec[key], fieldMap[key])}
                    </td>
                  ))}
                </tr>
              );
            })}

            {!safeResult.grouped && visibleFlatRows.map((rec) => (
              <tr key={rec.id} className="border-b border-slate-50 hover:bg-slate-50">
                {displayColumns.map((key) => (
                  <td key={key} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">
                    {formatValue(rec[key], fieldMap[key])}
                  </td>
                ))}
              </tr>
            ))}

            {previewTruncated && (
              <tr><td colSpan={displayColumns.length || 1}
                className="px-3 py-2 text-center text-[11px] text-slate-400 bg-slate-50 border-t border-slate-100">
                Preview limited to {PREVIEW_LIMIT} rows — Run Report or Export to see all {safeResult.recordCount.toLocaleString()} records.
              </td></tr>
            )}

            {safeResult.recordCount === 0 && (
              <tr><td colSpan={displayColumns.length || 1} className="px-3 py-12 text-center text-slate-400">
                No records match the current filters.
              </td></tr>
            )}
          </tbody>
          {summaryKeys.length > 0 && safeResult.recordCount > 0 && (
            <tfoot className="sticky bottom-0">
              <tr className="bg-slate-800 text-white">
                <td colSpan={displayColumns.length || 1} className="px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold uppercase tracking-wide">Grand Total</span>
                    <span className="text-[11px] bg-white/15 px-1.5 py-0.5 rounded">{safeResult.recordCount} records</span>
                    {summaryKeys.map((k) => (
                      <span key={k} className="text-[11px] font-semibold bg-white/20 px-2 py-0.5 rounded">
                        {AGG_LABEL[config.summaries[k]]} {displayLabel(fieldMap[k])}: {formatAggValue(safeResult.grandTotals[k], k, config.summaries[k], fieldMap)}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {paged && !safeResult.grouped && totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-white">
          <span className="text-xs text-slate-500">
            {((safePage - 1) * PAGE_SIZE) + 1}–{Math.min(safePage * PAGE_SIZE, safeResult.rows.length)} of {safeResult.rows.length.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button disabled={safePage <= 1} onClick={() => setTablePage(safePage - 1)}
              className="flex items-center gap-0.5 text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs text-slate-500 px-2">Page {safePage} / {totalPages}</span>
            <button disabled={safePage >= totalPages} onClick={() => setTablePage(safePage + 1)}
              className="flex items-center gap-0.5 text-xs px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
